<template lang="pug">
.publication-actions
  nuxt-link.action-link(v-if='showDetails', :to='localePath(publication.detailPath)') {{ detailsLabel ?? t('pages.publications.details') }}
  a.action-link(
    v-if='publication.fullTextUrl'
    :href='publication.fullTextUrl'
    target='_blank'
    rel='noopener noreferrer'
  ) {{ t('pages.publications.fullText') }}
  a.action-link(
    v-if='publication.doiUrl'
    :href='publication.doiUrl'
    target='_blank'
    rel='noopener noreferrer'
  ) DOI
  button.action-link.action-link--button(
    v-if='publication.apa7'
    type='button'
    @click.prevent='copyReference(`apa7-${publication.id}`, publication.apa7)'
  ) {{ copiedReferenceId === `apa7-${publication.id}` ? t('pages.publications.copiedApa7') : (apaCopyLabel ?? t('pages.publications.copyApa7')) }}
  a.action-link(
    v-for='link in additionalLinks'
    :key='link.url'
    :href='link.url'
    target='_blank'
    rel='noopener noreferrer'
  ) {{ link.label }}
  button.action-link.action-link--button(
    v-if='publication.bibtex'
    type='button'
    @click.prevent='copyReference(`bibtex-${publication.id}`, publication.bibtex)'
  ) {{ copiedReferenceId === `bibtex-${publication.id}` ? t('pages.publications.copiedBibtex') : (bibtexCopyLabel ?? t('pages.publications.copyBibtex')) }}
</template>

<script setup lang="ts">
import type { PublicationEntry } from '~/services/publicationsService'

defineOptions({
  name: 'PublicationActions',
})

const props = withDefaults(
  defineProps<{
    publication: PublicationEntry
    showDetails?: boolean
    showAdditionalLinks?: boolean
    detailsLabel?: string | null
    apaCopyLabel?: string | null
    bibtexCopyLabel?: string | null
  }>(),
  {
    showDetails: false,
    showAdditionalLinks: false,
    detailsLabel: null,
    apaCopyLabel: null,
    bibtexCopyLabel: null,
  },
)

const { copiedReferenceId, copyReference } = useClipboardCopy()
const { t } = useI18n()
const localePath = useLocalePath()

const additionalLinks = computed(() => {
  if (!props.showAdditionalLinks) {
    return []
  }

  return props.publication.links.filter(
    (link) =>
      link.url !== props.publication.fullTextUrl &&
      link.url !== props.publication.doiUrl,
  )
})
</script>

<style scoped>
.publication-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 0.9rem;
}

.action-link {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--brand-primary);
  cursor: pointer;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
}

.action-link:hover {
  color: color-mix(in srgb, var(--brand-primary) 78%, black);
  text-decoration: underline;
}

.action-link--button {
  appearance: none;
}
</style>
